import { map, ReplaySubject, BehaviorSubject, of } from 'rxjs';
import * as i0 from '@angular/core';
import { inject, Injectable, NgModule } from '@angular/core';
import { LiveUpdateHub, MockLiveUpdateHub } from '@tc/live-updates';
import { EnvironmentService } from '@tc/foundation';
import { TokenService } from '@tc/security-oauth-legacy';
import { LoggerService } from '@tc/logger';
import { first, map as map$1 } from 'rxjs/operators';
class PermissionLiveUpdateHub extends LiveUpdateHub {
  constructor() {
    const envService = inject(EnvironmentService);
    const tokenService = inject(TokenService);
    const loggingService = inject(LoggerService);
    super(envService, loggingService, 'permission', 'permissions', tokenService.token$.pipe(map(token => token?.token ?? '')));
  }
  static {
    this.ɵfac = function PermissionLiveUpdateHub_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PermissionLiveUpdateHub)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: PermissionLiveUpdateHub,
      factory: PermissionLiveUpdateHub.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PermissionLiveUpdateHub, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [], null);
})();
class PermissionService {
  constructor() {
    this.permissionLiveUpdateHub = inject(PermissionLiveUpdateHub);
    this.permissionsSubject = new ReplaySubject(1);
    this.permissions$ = this.permissionsSubject.asObservable();
    this.permissionsFromHub = [];
    this.permissionsUpdatedSubject = new BehaviorSubject(false);
    this.permissionsUpdated$ = this.permissionsUpdatedSubject.asObservable();
    this.permissionsSubscription = this.permissionLiveUpdateHub.hubUpdated$.subscribe(permissions => this.setPermissions(permissions));
  }
  ngOnDestroy() {
    this.permissionsSubscription.unsubscribe();
  }
  hasPermissionFor(...permissions) {
    return this.permissionsFromHub.some(p => permissions.includes(p));
  }
  setPermissions(permissions) {
    if (permissions == null) {
      this.permissionsFromHub = [];
      this.permissionsSubject.next([]);
    } else {
      this.permissionsFromHub = permissions;
      this.permissionsSubject.next(permissions);
    }
    this.permissionsUpdatedSubject.next(true);
  }
  static {
    this.ɵfac = function PermissionService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PermissionService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: PermissionService,
      factory: PermissionService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PermissionService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [], null);
})();
class PermissionModule {
  static {
    this.ɵfac = function PermissionModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PermissionModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: PermissionModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PermissionModule, [{
    type: NgModule
  }], null, null);
})();
class MockPermissionService {
  constructor(...permissions) {
    this.permissionsSubject = new ReplaySubject(1);
    this.permissions$ = this.permissionsSubject.asObservable();
    this.permissionsFromHub = [];
    this.permissionsUpdatedSubject = new BehaviorSubject(false);
    this.permissionsUpdated$ = this.permissionsUpdatedSubject.asObservable();
    this.setPermissions(permissions);
  }
  hasPermissionFor(...permissions) {
    return this.permissionsFromHub.some(p => permissions.includes(p));
  }
  setPermissions(permissions) {
    if (permissions == null) {
      this.permissionsFromHub = [];
      this.permissionsSubject.next([]);
    } else {
      this.permissionsFromHub = permissions;
      this.permissionsSubject.next(permissions);
    }
    this.permissionsUpdatedSubject.next(true);
  }
}
class MockPermissionLiveUpdateHub extends MockLiveUpdateHub {
  constructor(permissions) {
    super();
    this.hubUpdated$ = of(permissions);
  }
}
var RouteGuardPermissionOperatorType;
(function (RouteGuardPermissionOperatorType) {
  RouteGuardPermissionOperatorType[RouteGuardPermissionOperatorType["AND"] = 0] = "AND";
  RouteGuardPermissionOperatorType[RouteGuardPermissionOperatorType["OR"] = 1] = "OR";
})(RouteGuardPermissionOperatorType || (RouteGuardPermissionOperatorType = {}));
class PermissionGuard {
  constructor() {
    this.service = inject(PermissionService);
  }
  canActivate(route, _state) {
    const routeGuardPermissionData = route.data;
    if (routeGuardPermissionData == null || routeGuardPermissionData.permissions.length === 0) {
      return of(false);
    }
    return this.service.permissions$.pipe(first(), map$1(userPermissions => {
      if (routeGuardPermissionData.operatorType === RouteGuardPermissionOperatorType.OR) {
        return routeGuardPermissionData.permissions.some(rp => userPermissions.indexOf(rp) > -1);
      }
      return routeGuardPermissionData.permissions.every(rp => userPermissions.indexOf(rp) > -1);
    }));
  }
  static {
    this.ɵfac = function PermissionGuard_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || PermissionGuard)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: PermissionGuard,
      factory: PermissionGuard.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PermissionGuard, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], null, null);
})();
class RouteGuardPermissionData {
  constructor(operatorType, ...permissions) {
    this.operatorType = operatorType;
    this.permissions = permissions;
  }
}

/**
 * Generated bundle index. Do not edit.
 */

export { MockPermissionLiveUpdateHub, MockPermissionService, PermissionGuard, PermissionLiveUpdateHub, PermissionModule, PermissionService, RouteGuardPermissionData, RouteGuardPermissionOperatorType };
